import type { MDXEditorMethods } from '@mdxeditor/editor';
import type { UseFormReturn } from 'react-hook-form';
import { useCallback, useEffect, useRef } from 'react';
import {
  Labeled,
  OptionCard,
  Radio,
  RadioGroup,
  TextInputField,
} from '@sixui/ui';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IArticleFormValues } from './ArticleForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import {
  IframeMdxDirectiveDescriptor,
  iframeMdxDirectiveRemarkPlugin,
  IframeMdxDirectiveToolbarButton,
} from '~/components/mdxDirectives/IframeMdxDirective';
import {
  PollMdxDirectiveDescriptor,
  pollMdxDirectiveRemarkPlugin,
  PollMdxDirectiveToolbarButton,
} from '~/components/mdxDirectives/PollMdxDirective';
import {
  WPPlaylistMdxDirectiveDescriptor,
  wpPlaylistMdxDirectiveRemarkPlugin,
  WPPlaylistMdxDirectiveToolbarButton,
} from '~/components/mdxDirectives/WPPlaylistMdxDirective';
import {
  YouTubeMdxDirectiveDescriptor,
  youTubeMdxDirectiveRemarkPlugin,
  YouTubeMdxDirectiveToolbarButton,
} from '~/components/mdxDirectives/YouTubeMdxDirective';
import { CategoriesSelector } from '~/components/organisms/fields/CategoriesSelector';
import { MarkdownEditor } from '~/components/organisms/fields/MarkdownEditor';
import { PollSelector } from '~/components/organisms/fields/PollSelector';
import { markdownToHtml } from '~/helpers/markdownToHtml';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';

export type IArticleFormMainSectionProps = IContainerProps & {
  mode: IFormMode;
  form: UseFormReturn<IArticleFormValues>;
  appId: string;
  markdownEditorRef?: React.RefObject<MDXEditorMethods>;
  defaultMarkdown?: string;
};

const styles = stylex.create({
  typeOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridAutoRows: '1fr',
    gap: spacingTokens.padding$2,
  },
});

export const ArticleFormMainSection: React.FC<IArticleFormMainSectionProps> = (
  props,
) => {
  const { sx, form, appId, markdownEditorRef, defaultMarkdown } = props;
  const { t } = useTranslation();
  const appQuery = useAppQuery({ appId });

  const updateHtml = useCallback(
    (markdown: string): Promise<void> =>
      markdownToHtml(
        markdown,
        [
          wpPlaylistMdxDirectiveRemarkPlugin,
          youTubeMdxDirectiveRemarkPlugin,
          iframeMdxDirectiveRemarkPlugin,
          pollMdxDirectiveRemarkPlugin,
        ],
        { allowDangerousHtml: true },
      ).then((html) => {
        form.setValue('html', html);
      }),
    [form],
  );

  const isInitRef = useRef(false);
  useEffect(() => {
    if (defaultMarkdown && !isInitRef.current) {
      void updateHtml(defaultMarkdown);
      isInitRef.current = true;
    }
  }, [updateHtml, defaultMarkdown]);

  const handleMarkdownChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => void updateHtml(event.target.value);

  const wpPlaylistsActivated =
    !!appQuery?.data?.settings?.playlistManagementUrl;

  return (
    <FormSection sx={sx}>
      <Labeled label={t('common.title')} required>
        <FormField
          name="title"
          control={form.control}
          as={TextInputField}
          forwardError
        />
      </Labeled>
      <Labeled label={t('ArticleFormMainSection.fields.type.label')} required>
        <FormField
          name="type"
          control={form.control}
          as={RadioGroup}
          rules={{ required: true }}
          sx={styles.typeOptions}
        >
          <OptionCard
            as={Radio}
            label={t(
              'ArticleFormMainSection.fields.type.options.article.label',
            )}
            value="article"
          >
            {t(
              'ArticleFormMainSection.fields.type.options.article.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t(
              'ArticleFormMainSection.fields.type.options.webview.label',
            )}
            value="webview"
          >
            {t(
              'ArticleFormMainSection.fields.type.options.webview.supportingText',
            )}
          </OptionCard>
          <OptionCard
            as={Radio}
            label={t('ArticleFormMainSection.fields.type.options.poll.label')}
            value="poll"
          >
            {t(
              'ArticleFormMainSection.fields.type.options.poll.supportingText',
            )}
          </OptionCard>
        </FormField>
      </Labeled>

      {form.watch('type') === 'webview' ? (
        <Labeled label={t('ArticleFormMainSection.fields.url.label')} required>
          <FormField
            name="markdown"
            control={form.control}
            as={TextInputField}
            type="url"
            forwardError
          />
        </Labeled>
      ) : form.watch('type') === 'poll' ? (
        <Labeled label={t('ArticleFormMainSection.fields.poll.label')} required>
          <FormField
            appId={appId}
            name="markdown"
            control={form.control}
            as={PollSelector}
            forwardError
          />
        </Labeled>
      ) : (
        <Labeled label={t('common.text')} required>
          <FormField
            name="markdown"
            control={form.control}
            as={MarkdownEditor}
            markdown={defaultMarkdown}
            forwardError
            rules={{ onChange: handleMarkdownChange }}
            ref={markdownEditorRef}
            appId={appId}
            directivesDescriptors={[
              WPPlaylistMdxDirectiveDescriptor,
              YouTubeMdxDirectiveDescriptor,
              IframeMdxDirectiveDescriptor,
              PollMdxDirectiveDescriptor,
            ]}
            toolbarContents={
              <>
                {wpPlaylistsActivated ? (
                  <WPPlaylistMdxDirectiveToolbarButton appId={appId} />
                ) : undefined}
                <YouTubeMdxDirectiveToolbarButton />
                <IframeMdxDirectiveToolbarButton />
                <PollMdxDirectiveToolbarButton appId={appId} />
              </>
            }
          />
        </Labeled>
      )}
      <Labeled
        label={t('ArticleFormMainSection.fields.categoryIds.label')}
        required
      >
        <FormField
          name="categoryIds"
          control={form.control}
          as={CategoriesSelector}
          forwardError
          appId={appId}
          filter={{ includeTypes: ['category'] }}
          emptyValue={[]}
        />
      </Labeled>
    </FormSection>
  );
};
